import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminApiRequest } from "../utils/adminApi";
import Modal from "./Modal";
import PrintItemModal from "./prints/PrintItemModal";

const freshForm = () => ({
  category_id: "",
  size: "",
  extras: [],
  groups: [
    { group_label: "", bundles: [{ qty: "", price_single: "", price_double: "" }] },
  ],
});

export default function Prints() {
  const [prints, setPrints] = useState([]);
  const [categories, setCategories] = useState([]);
  const [masterExtras, setMasterExtras] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [editingGroup, setEditingGroup] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [form, setForm] = useState(freshForm);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [pRes, cRes, eRes] = await Promise.all([
        adminApiRequest({ script: "get_prints" }),
        adminApiRequest({ script: "list_print_categories" }),
        adminApiRequest({ script: "list_print_extras" }),
      ]);

      if (cRes?.success) {
        setCategories(Array.isArray(cRes.data) ? cRes.data : []);
      }

      if (eRes?.success) {
        setMasterExtras(Array.isArray(eRes.data) ? eRes.data : []);
      }

      if (pRes?.success) {
        setPrints(Array.isArray(pRes.data) ? pRes.data : []);
      }
    } catch (err) {
      setError("Unable to load data.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Only reload the extras list (not full page data) — used mid-save
  // to refresh the master dropdown without re-fetching prints/categories
  const reloadExtras = useCallback(async () => {
    try {
      const eRes = await adminApiRequest({ script: "list_print_extras" });
      if (eRes?.success) {
        setMasterExtras(Array.isArray(eRes.data) ? eRes.data : []);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const categoryGroups = useMemo(() => {
    const groups = {};

    // Initialize groups for all categories to ensure they show up (even if empty)
    categories.forEach((cat) => {
      groups[cat.id] = {
        categoryName: cat.name,
        categoryId: cat.id,
        items: {}, // Map of size -> group details
      };
    });

    prints.forEach((p) => {
      const catId = p.category_id;
      if (!groups[catId]) return;

      const sizeKey = p.size || "Standard";
      if (!groups[catId].items[sizeKey]) {
        groups[catId].items[sizeKey] = {
          category_id: catId,
          size: p.size,
          items: [],
          extras: p.extras || [],
        };
      }
      groups[catId].items[sizeKey].items.push(p);
    });

    return Object.values(groups).sort((a, b) =>
      a.categoryName.localeCompare(b.categoryName),
    );
  }, [prints, categories]);



  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(""), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const openCreateModal = () => {
    setForm(freshForm());
    setEditingGroup(null);
    setIsModalOpen(true);
  };

  const openEditModal = (group) => {
    const fmt = (v) => { const n = parseFloat(v); return isNaN(n) ? "0.00" : n.toFixed(2); };
    setEditingGroup(group);
    const groupMap = {};
    group.items.forEach((item) => {
      if (!groupMap[item.group_label]) {
        groupMap[item.group_label] = { group_label: item.group_label, bundles: [] };
      }
      groupMap[item.group_label].bundles.push({
        id: item.id,
        qty: item.qty,
        price_single: fmt(item.price_single),
        price_double: item.price_double !== null && item.price_double !== "" ? fmt(item.price_double) : "",
      });
    });
    setForm({
      category_id: group.category_id,
      size: group.size,
      extras: (group.extras || []).map((ex) => ({ ...ex, price: fmt(ex.price) })),
      groups: Object.values(groupMap),
    });
    setIsModalOpen(true);
  };

  const openCopyModal = (group) => {
    const fmt = (v) => {
      const n = parseFloat(v);
      return isNaN(n) ? "0.00" : n.toFixed(2);
    };

    // We set editingGroup to null so it's treated as a new item
    setEditingGroup(null);
    const groupMap = {};
    group.items.forEach((item) => {
      if (!groupMap[item.group_label]) {
        groupMap[item.group_label] = {
          group_label: item.group_label,
          bundles: [],
        };
      }
      groupMap[item.group_label].bundles.push({
        // No ID here so it creates a new record
        qty: item.qty,
        price_single: fmt(item.price_single),
        price_double:
          item.price_double !== null && item.price_double !== ""
            ? fmt(item.price_double)
            : "",
      });
    });
    setForm({
      category_id: group.category_id,
      size: group.size + " (Copy)", // Suggest it's a copy
      extras: (group.extras || []).map((ex) => ({
        ...ex,
        price: fmt(ex.price),
      })),
      groups: Object.values(groupMap),
    });
    setIsModalOpen(true);
  };

  const openDeleteModal = (group) => {
    setDeleteTarget(group);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async (e) => {
    if (e) e.preventDefault();
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      const res = await adminApiRequest({
        script: "delete_print_group",
        data: {
          category_id: deleteTarget.category_id,
          size: deleteTarget.size,
        },
      });
      if (!res?.success) throw new Error(res?.message || "Failed to delete.");
      setSuccess("Item deleted successfully.");
      setIsDeleteModalOpen(false);
      await loadData();
    } catch (err) {
      setError(err.message || "Failed to delete item.");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    try {
      setSaving(true);
      setError("");
      const cleanExtras = form.extras
        .filter((ex) => ex.name.trim() !== "")
        .map((ex) => ({
          id: ex.id || null,
          name: ex.name.trim(),
          price: Number(ex.price),
        }));

      const allBundles = [];
      form.groups.forEach((group) => {
        group.bundles.forEach((bundle) => {
          if (bundle.qty && bundle.price_single !== "") {
            allBundles.push({
              group_label: group.group_label.trim(),
              qty: Number(bundle.qty),
              price_single: Number(bundle.price_single),
              price_double:
                bundle.price_double !== "" ? Number(bundle.price_double) : null,
              extras: cleanExtras,
            });
          }
        });
      });

      const res = await adminApiRequest({
        script: "create_print",
        data: { 
          category_id: form.category_id, 
          size: form.size.trim(), 
          bundles: allBundles,
          extras: cleanExtras,
          is_matrix_edit: !!editingGroup,
          old_category_id: editingGroup?.category_id || null,
          old_size: editingGroup?.size || null
        },
      });

      if (!res?.success) throw new Error(res?.message || "Failed to save.");
      setSuccess("Item saved successfully.");
      setIsModalOpen(false);
      setForm(freshForm());
      setEditingGroup(null);
      await loadData();
    } catch (err) {
      setError(err.message || "Unable to save item.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="h-full min-h-0 flex flex-col bg-slate-50/50">
      <div className="flex-none bg-white border-b px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-medium text-slate-800">Print Items</h2>
            <p className="text-sm text-slate-500">
              Manage your print services grouped by category.
            </p>
          </div>
          <button
            onClick={openCreateModal}
            className="bg-slate-900 text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-slate-800 transition shadow-sm"
          >
            Add Item
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-6">
        {(error || success) && (
          <div className="mb-6">
            {error && (
              <div className="rounded-xl border border-red-100 bg-red-50/50 px-4 py-3 text-sm text-red-600">
                {error}
              </div>
            )}
            {success && (
              <div className="rounded-xl border border-green-100 bg-green-50/50 px-4 py-3 text-sm text-green-600">
                {success}
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-slate-800"></div>
          </div>
        ) : (
          <div className="space-y-12">
            {categoryGroups
              .filter((catGroup) => Object.keys(catGroup.items).length > 0)
              .map((catGroup) => {
                const sizes = Object.values(catGroup.items).sort((a, b) =>
                  a.size.localeCompare(b.size),
                );
                return (
                  <div key={catGroup.categoryId} className="space-y-4">
                    <div className="flex items-center gap-3">
                      <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500">
                        {catGroup.categoryName}
                      </h3>
                      <div className="h-px flex-1 bg-slate-200"></div>
                     
                    </div>

                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50/50 border-b border-slate-200">
                          <tr className="text-slate-500 font-medium text-xs">
                            <th className="px-6 py-3">Option</th>
                            <th className="px-6 py-3 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-slate-700">
                          {sizes.map((group, idx) => (
                            <tr
                              key={idx}
                              className="hover:bg-slate-50/50 transition-colors"
                            >
                              <td className="px-6 py-4 font-medium text-slate-900">
                                {group.size || "Standard"}
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => openEditModal(group)}
                                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => openCopyModal(group)}
                                    className="rounded-lg border border-blue-300 px-3 py-1.5 text-xs font-medium text-blue-600 transition hover:bg-blue-50"
                                  >
                                    Copy
                                  </button>
                                  <button
                                    onClick={() => openDeleteModal(group)}
                                    className="rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-50"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      <PrintItemModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setForm(freshForm());
          setEditingGroup(null);
          setError("");
          setSuccess("");
        }}
        onSubmit={handleSubmit}
        form={form}
        setForm={setForm}
        saving={saving}
        isEdit={!!editingGroup}
        categories={categories}
        masterExtras={masterExtras}
        onReloadExtras={reloadExtras}
        error={error}
        success={success}
      />

      {isDeleteModalOpen && (
        <Modal
          title="Delete Item"
          onClose={() => {
            setIsDeleteModalOpen(false);
            setError("");
            setSuccess("");
          }}
          onSubmit={handleDelete}
          submitButtonText={deleting ? "Deleting..." : "Delete Item"}
          showCancel={true}
          showSubmitButton={true}
          disableSubmit={deleting}
          submitButtonClass="bg-red-600 hover:bg-red-700 text-white"
          error={error}
        >
          <div className="py-2 text-sm text-slate-600 leading-relaxed">
            Are you sure you want to delete the{" "}
            <span className="font-semibold text-slate-900">
              {deleteTarget?.size || "Standard"}
            </span>{" "}
            package group? This action will permanently remove all pricing
            bundles and weight variations associated with this item.
          </div>
        </Modal>
      )}
    </section>
  );
}
